import React, { useState, useEffect, useMemo } from 'react';
import styles from './Integrations.module.css';
import SquareCardForm from './SquareCardForm';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Integrations({ API, authHeader, showToast }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [banner, setBanner] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);

  // Check URL params for OAuth callback result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === '1') {
      setBanner({ type: 'success', msg: 'Square connected successfully.' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('error')) {
      setBanner({ type: 'error', msg: `Square connection failed: ${params.get('error')}` });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/integrations/square/status`, { headers: { ...authHeader() } });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const fetchCatalog = async () => {
    setCatalogLoading(true);
    setCatalogError(null);
    try {
      const res = await fetch(`${API}/integrations/square/catalog`, { headers: { ...authHeader() } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load catalog');
      setCatalog(data);
    } catch (err) {
      setCatalogError(err.message);
    } finally {
      setCatalogLoading(false);
    }
  };

  // Auto-fetch catalog when connected
  useEffect(() => {
    if (status?.connected && !catalog) fetchCatalog();
  }, [status?.connected]);

  const handleConnect = () => {
    // Pass JWT as query param — browser redirect can't carry Authorization header
    const token = localStorage.getItem('ngs_token');
    window.location.href = `${API}/auth/square/authorize?token=${encodeURIComponent(token)}`;
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Square? Future bookings and payments will not sync until you reconnect.')) return;
    setDisconnecting(true);
    try {
      const res = await fetch(`${API}/integrations/square/disconnect`, {
        method: 'POST',
        headers: { ...authHeader() },
      });
      if (!res.ok) throw new Error();
      showToast('Square disconnected');
      fetchStatus();
    } catch {
      showToast('Disconnect failed', 'error');
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className={styles.page}>
      {banner && (
        <div className={banner.type === 'success' ? styles.successBanner : styles.errorBanner}>
          {banner.msg}
        </div>
      )}

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardIcon}>□</div>
          <div>
            <h2 className={styles.cardTitle}>Square</h2>
            <p className={styles.cardSubtitle}>Booking, payments, and customer sync</p>
          </div>
        </div>

        {loading && <div className={styles.spinner} />}

        {!loading && status && !status.configured && (
          <div className={styles.warnBanner}>
            Square is not configured on the server. Set SQUARE_APP_ID, SQUARE_APP_SECRET, and SQUARE_ENVIRONMENT environment variables.
          </div>
        )}

        {!loading && status && status.configured && !status.connected && (
          <>
            <span className={`${styles.status} ${styles.statusDisconnected}`}>
              ○ Not Connected
            </span>
            <p className={styles.info}>
              Connect your Square account to enable online booking and auto-credit from card payments.
              Clicking Connect will redirect you to Square to authorize access.
            </p>
            <div className={styles.btnRow}>
              <button className={styles.connectBtn} onClick={handleConnect}>
                Connect Square &rarr;
              </button>
            </div>
          </>
        )}

        {!loading && status && status.connected && (
          <>
            <span className={`${styles.status} ${styles.statusConnected}`}>
              ✓ Connected
              <span className={`${styles.envBadge} ${status.environment === 'production' ? styles.envBadgeProd : ''}`}>
                {status.environment || 'sandbox'}
              </span>
            </span>
            <div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Merchant</span>
                <span className={styles.detailValue}>{status.merchantName || status.merchantId || '—'}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Location</span>
                <span className={styles.detailValue}>{status.mainLocationName || status.mainLocationId || '—'}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Connected</span>
                <span className={styles.detailValue}>{fmtDate(status.connectedAt)}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Token Expires</span>
                <span className={styles.detailValue}>{fmtDate(status.expiresAt)}</span>
              </div>
            </div>
            <div className={styles.btnRow}>
              <button className={styles.disconnectBtn} disabled={disconnecting} onClick={handleDisconnect}>
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
              <button className={styles.connectBtn} onClick={fetchCatalog} disabled={catalogLoading} style={{ background: 'var(--surface-3)', color: 'var(--gray-1)', border: '1px solid var(--border)' }}>
                {catalogLoading ? 'Refreshing...' : 'Refresh Catalog'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* ══ Catalog preview ══ */}
      {status?.connected && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon} style={{ background: 'var(--orange)', color: '#fff' }}>⌕</div>
            <div>
              <h2 className={styles.cardTitle}>Square Catalog</h2>
              <p className={styles.cardSubtitle}>Live data pulled from your connected Square account</p>
            </div>
          </div>

          {catalogLoading && <div className={styles.spinner} />}
          {catalogError && <div className={styles.errorBanner}>Could not load catalog: {catalogError}</div>}

          {catalog && !catalogLoading && (
            <>
              {/* Summary counts */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
                <div style={{ flex: '1 1 100px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 18px' }}>
                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: 'var(--gray-2)', letterSpacing: '0.08em', marginBottom: 4 }}>Locations</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--orange)', fontFamily: 'var(--font-display)' }}>{catalog.locations?.length || 0}</div>
                </div>
                <div style={{ flex: '1 1 100px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 18px' }}>
                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: 'var(--gray-2)', letterSpacing: '0.08em', marginBottom: 4 }}>Team Members</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--orange)', fontFamily: 'var(--font-display)' }}>{catalog.teamMembers?.length || 0}</div>
                </div>
                <div style={{ flex: '1 1 100px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 18px' }}>
                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: 'var(--gray-2)', letterSpacing: '0.08em', marginBottom: 4 }}>Bookable Staff</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--orange)', fontFamily: 'var(--font-display)' }}>{catalog.bookingProfiles?.filter(b => b.isBookable)?.length || 0}</div>
                </div>
                <div style={{ flex: '1 1 100px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 18px' }}>
                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: 'var(--gray-2)', letterSpacing: '0.08em', marginBottom: 4 }}>Services</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--orange)', fontFamily: 'var(--font-display)' }}>{catalog.services?.length || 0}</div>
                </div>
              </div>

              {catalog.bpError && (
                <div className={styles.errorBanner} style={{ marginBottom: 20 }}>
                  Booking profiles call failed: {catalog.bpError}
                </div>
              )}

              {/* Locations */}
              {catalog.locations?.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gray-2)', marginBottom: 10 }}>Locations</div>
                  {catalog.locations.map(l => (
                    <div key={l.id} className={styles.detailRow}>
                      <span className={styles.detailLabel}>{l.name}{l.status && l.status !== 'ACTIVE' ? ` (${l.status})` : ''}</span>
                      <span className={styles.detailValue} style={{ fontSize: '0.8rem' }}>{l.addressLines || l.id}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Team members */}
              {catalog.teamMembers?.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gray-2)', marginBottom: 10 }}>Team Members</div>
                  {catalog.teamMembers.map(t => {
                    const bp = catalog.bookingProfiles?.find(b => b.teamMemberId === t.id);
                    return (
                      <div key={t.id} className={styles.detailRow}>
                        <span className={styles.detailLabel}>
                          {[t.givenName, t.familyName].filter(Boolean).join(' ') || t.id}
                          {t.isOwner && <span style={{ marginLeft: 8, fontSize: '0.65rem', padding: '2px 6px', background: 'var(--orange-dim)', color: 'var(--orange)', borderRadius: 4, fontWeight: 700 }}>OWNER</span>}
                        </span>
                        <span className={styles.detailValue} style={{ fontSize: '0.8rem' }}>
                          {bp?.isBookable ? (
                            <span style={{ color: 'var(--green)' }}>✓ Bookable online</span>
                          ) : (
                            <span style={{ color: 'var(--gray-3)' }}>Not bookable</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Services */}
              {catalog.services?.length > 0 ? (
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gray-2)', marginBottom: 10 }}>Services</div>
                  {catalog.services.map(s => (
                    <div key={s.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontWeight: 700, color: 'var(--gray-1)', marginBottom: 4 }}>{s.name}</div>
                      {s.description && <div style={{ fontSize: '0.82rem', color: 'var(--gray-2)', marginBottom: 8 }}>{s.description}</div>}
                      {s.variations.map(v => (
                        <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '4px 0 4px 12px' }}>
                          <span style={{ color: 'var(--gray-2)' }}>
                            {v.name || 'Default'}
                            {v.availableForBooking === false && <span style={{ color: 'var(--yellow)', marginLeft: 6 }}>(not bookable)</span>}
                          </span>
                          <span style={{ color: 'var(--gray-1)', fontWeight: 600 }}>
                            {v.priceCents != null ? `$${(v.priceCents / 100).toFixed(2)}` : 'No price'}
                            {v.serviceDurationMs ? ` · ${Math.round(v.serviceDurationMs / 60000)} min` : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.warnBanner}>
                  No services found. In your Square sandbox seller dashboard, go to Appointments → Services and add at least one service that's bookable online.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {status?.connected && catalog && (
        <TestBooking API={API} authHeader={authHeader} catalog={catalog} showToast={showToast} />
      )}

      {status?.connected && (
        <TestPayment API={API} authHeader={authHeader} showToast={showToast} />
      )}
    </div>
  );
}

// ─── Test Payment card (Phase 4) ─────────────────────────────────────────────
function TestPayment({ API, authHeader, showToast }) {
  const cardRef = React.useRef(null);
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [amountUsd, setAmountUsd] = useState('1.00');
  const [note, setNote] = useState('NGS test payment');
  const [charging, setCharging] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/integrations/square/payments-config`, { headers: { ...authHeader() } });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load payments config');
        if (!data.locationId) throw new Error('No active Square location found');
        setConfig(data);
      } catch (e) {
        setConfigError(e.message);
      }
    })();
  }, []);

  const charge = async () => {
    setError(null);
    setResult(null);
    const amountCents = Math.round(parseFloat(amountUsd) * 100);
    if (!amountCents || amountCents <= 0) { setError('Enter a positive amount'); return; }
    setCharging(true);
    try {
      const token = await cardRef.current.tokenize();
      const res = await fetch(`${API}/square/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          sourceId: token,
          amountCents,
          locationId: config.locationId,
          note,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Payment failed');
      setResult(data.payment);
      showToast?.('Test payment succeeded', 'success');
    } catch (e) {
      setError(e.message);
    } finally {
      setCharging(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardIcon} style={{ background: 'var(--orange)', color: '#fff' }}>$</div>
        <div>
          <h2 className={styles.cardTitle}>Test Payment</h2>
          <p className={styles.cardSubtitle}>Charge a card via Square's hosted form. Sandbox test card: 4111 1111 1111 1111 · any future expiry · any CVV · any ZIP</p>
        </div>
      </div>

      {configError && <div className={styles.errorBanner}>{configError}</div>}

      {config && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 14 }}>
            <label>
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-2)', marginBottom: 6 }}>Amount (USD)</div>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={amountUsd}
                onChange={e => setAmountUsd(e.target.value)}
                style={selectStyle}
              />
            </label>
            <label>
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-2)', marginBottom: 6 }}>Note</div>
              <input value={note} onChange={e => setNote(e.target.value)} style={selectStyle} />
            </label>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-2)', marginBottom: 6 }}>Card</div>
            <SquareCardForm ref={cardRef} config={config} />
          </div>

          <button className={styles.connectBtn} onClick={charge} disabled={charging}>
            {charging ? 'Charging…' : `Charge $${parseFloat(amountUsd || 0).toFixed(2)}`}
          </button>

          {error && <div className={styles.errorBanner} style={{ marginTop: 14 }}>{error}</div>}
          {result && (
            <div style={{ marginTop: 16, padding: 14, background: 'var(--surface-2)', border: '1px solid var(--green)', borderRadius: 8 }}>
              <div style={{ fontWeight: 700, color: 'var(--green)', marginBottom: 6 }}>✓ Payment {result.status}</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--gray-1)' }}>
                <div><strong>Payment ID:</strong> {result.id}</div>
                <div><strong>Amount:</strong> ${(Number(result.amountMoney?.amount || 0) / 100).toFixed(2)} {result.amountMoney?.currency}</div>
                <div><strong>Card:</strong> {result.cardDetails?.card?.cardBrand} •••• {result.cardDetails?.card?.last4}</div>
                <div><strong>Receipt URL:</strong> {result.receiptUrl ? <a href={result.receiptUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--orange)' }}>Open</a> : '—'}</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Test Booking card (Phase 3) ─────────────────────────────────────────────
function TestBooking({ API, authHeader, catalog, showToast }) {
  const flatVariations = useMemo(() => {
    const list = [];
    (catalog.services || []).forEach(s => {
      (s.variations || []).forEach(v => {
        list.push({
          key: v.id,
          serviceName: s.name,
          variationName: v.name,
          version: v.version,
          priceCents: v.priceCents,
          durationMs: v.serviceDurationMs,
          availableForBooking: v.availableForBooking,
        });
      });
    });
    return list.filter(v => v.availableForBooking !== false);
  }, [catalog]);

  const defaultLocation = catalog.locations?.[0]?.id || '';

  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [variationId, setVariationId] = useState(flatVariations[0]?.key || '');
  const [teamMemberId, setTeamMemberId] = useState('');
  const [locationId, setLocationId] = useState(defaultLocation);
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(null);
  const [customerName, setCustomerName] = useState('Test Customer');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerNote, setCustomerNote] = useState('');
  const [booking, setBooking] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStaffLoading(true);
      try {
        const res = await fetch(`${API}/square/staff`, { headers: { ...authHeader() } });
        const data = await res.json();
        if (!cancelled && res.ok) {
          setStaff(data.staff || []);
          if (!teamMemberId && data.staff?.[0]) setTeamMemberId(data.staff[0].teamMemberId);
        }
      } finally {
        if (!cancelled) setStaffLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedVariation = flatVariations.find(v => v.key === variationId);
  const durationMinutes = selectedVariation?.durationMs ? Math.round(selectedVariation.durationMs / 60000) : 30;

  const checkAvailability = async () => {
    if (!variationId || !locationId || !date) return;
    setSlotsLoading(true);
    setSlotsError(null);
    setSlots(null);
    setBooking(null);
    try {
      // Square requires >= 24h range. Query from start of local day through start of next day + 1h buffer.
      const startAt = new Date(`${date}T00:00:00`).toISOString();
      const endDate = new Date(`${date}T00:00:00`);
      endDate.setDate(endDate.getDate() + 1);
      endDate.setHours(endDate.getHours() + 1);
      const endAt = endDate.toISOString();

      const params = new URLSearchParams({
        serviceVariationId: variationId,
        locationId,
        startAt,
        endAt,
      });
      if (teamMemberId) params.set('teamMemberId', teamMemberId);

      const res = await fetch(`${API}/square/availability?${params.toString()}`, { headers: { ...authHeader() } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Availability search failed');
      // Filter to slots on the requested local date
      // Filter to local NGS date (America/Denver) — UTC slice drops slots after ~6pm MT.
      const localDateFmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Denver', year: 'numeric', month: '2-digit', day: '2-digit',
      });
      const onDate = (data.availabilities || []).filter(a => localDateFmt.format(new Date(a.startAt)) === date);
      setSlots(onDate);
    } catch (err) {
      setSlotsError(err.message);
    } finally {
      setSlotsLoading(false);
    }
  };

  const bookSlot = async (startAt) => {
    if (!variationId || !teamMemberId || !locationId || !startAt) return;
    setBookingLoading(true);
    setBookingError(null);
    setBooking(null);
    try {
      const body = {
        serviceVariationId: variationId,
        serviceVariationVersion: selectedVariation?.version,
        teamMemberId,
        locationId,
        startAt,
        durationMinutes,
        customerName,
        customerEmail: customerEmail || undefined,
        customerNote,
      };
      const res = await fetch(`${API}/square/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Booking failed');
      setBooking(data);
      showToast?.('Test booking created in Square sandbox', 'success');
    } catch (err) {
      setBookingError(err.message);
    } finally {
      setBookingLoading(false);
    }
  };

  const fmtSlot = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardIcon} style={{ background: 'var(--orange)', color: '#fff' }}>★</div>
        <div>
          <h2 className={styles.cardTitle}>Test Booking</h2>
          <p className={styles.cardSubtitle}>Pick a service, staff member, and date to check availability and create a sandbox booking</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginBottom: 16 }}>
        <label style={{ display: 'block' }}>
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-2)', marginBottom: 6 }}>Service</div>
          <select value={variationId} onChange={e => setVariationId(e.target.value)} style={selectStyle}>
            {flatVariations.map(v => (
              <option key={v.key} value={v.key}>
                {v.serviceName}{v.variationName && v.variationName !== 'Regular' ? ` — ${v.variationName}` : ''}
                {v.durationMs ? ` (${Math.round(v.durationMs / 60000)}min)` : ''}
                {v.priceCents != null ? ` $${(v.priceCents / 100).toFixed(2)}` : ''}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'block' }}>
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-2)', marginBottom: 6 }}>Staff</div>
          <select value={teamMemberId} onChange={e => setTeamMemberId(e.target.value)} style={selectStyle} disabled={staffLoading}>
            {staffLoading && <option>Loading…</option>}
            {!staffLoading && staff.length === 0 && <option value="">No bookable staff</option>}
            {staff.map(s => (
              <option key={s.teamMemberId} value={s.teamMemberId}>{s.displayName}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'block' }}>
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-2)', marginBottom: 6 }}>Location</div>
          <select value={locationId} onChange={e => setLocationId(e.target.value)} style={selectStyle}>
            {(catalog.locations || []).map(l => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'block' }}>
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-2)', marginBottom: 6 }}>Date</div>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={selectStyle} />
        </label>
      </div>

      <button className={styles.connectBtn} onClick={checkAvailability} disabled={slotsLoading || !variationId || !teamMemberId || !locationId}>
        {slotsLoading ? 'Checking…' : 'Check Availability'}
      </button>

      {slotsError && <div className={styles.errorBanner} style={{ marginTop: 14 }}>{slotsError}</div>}

      {slots && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gray-2)', marginBottom: 10 }}>
            {slots.length} open slot{slots.length === 1 ? '' : 's'} on {fmtDate(date + 'T12:00:00')}
          </div>
          {slots.length === 0 ? (
            <div className={styles.warnBanner}>No availability for this service/staff/date combination. Try a different date or check Square Appointments → Staff availability.</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {slots.map((s, i) => (
                <button
                  key={i}
                  onClick={() => bookSlot(s.startAt)}
                  disabled={bookingLoading}
                  style={{
                    padding: '10px 14px',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    color: 'var(--gray-1)',
                    fontWeight: 600,
                    cursor: bookingLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {fmtSlot(s.startAt)}
                </button>
              ))}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginTop: 10 }}>
            <label>
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-2)', marginBottom: 6 }}>Customer Name</div>
              <input value={customerName} onChange={e => setCustomerName(e.target.value)} style={selectStyle} />
            </label>
            <label>
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-2)', marginBottom: 6 }}>Customer Email (optional)</div>
              <input value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} style={selectStyle} placeholder="test@example.com" />
            </label>
            <label style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-2)', marginBottom: 6 }}>Note (optional)</div>
              <input value={customerNote} onChange={e => setCustomerNote(e.target.value)} style={selectStyle} placeholder="Test booking from NGS admin portal" />
            </label>
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--gray-2)', marginTop: 8 }}>Click a time slot above to create a test booking in Square with the customer info below.</div>
        </div>
      )}

      {bookingLoading && <div style={{ marginTop: 14 }}><div className={styles.spinner} /> Creating booking…</div>}
      {bookingError && <div className={styles.errorBanner} style={{ marginTop: 14 }}>{bookingError}</div>}
      {booking?.booking && (
        <div style={{ marginTop: 16, padding: 14, background: 'var(--surface-2)', border: '1px solid var(--green)', borderRadius: 8 }}>
          <div style={{ fontWeight: 700, color: 'var(--green)', marginBottom: 6 }}>✓ Booking created</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--gray-1)' }}>
            <div><strong>ID:</strong> {booking.booking.id}</div>
            <div><strong>Status:</strong> {booking.booking.status}</div>
            <div><strong>Start:</strong> {new Date(booking.booking.startAt).toLocaleString()}</div>
            <div><strong>Square Customer ID:</strong> {booking.squareCustomerId}</div>
          </div>
        </div>
      )}
    </div>
  );
}

const selectStyle = {
  width: '100%',
  padding: '10px 12px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  color: 'var(--gray-1)',
  fontSize: '0.9rem',
  fontFamily: 'inherit',
};
